export enum UserRole {
  Trader = 'TRADER',
  GroupOwner = 'GROUP_OWNER',
  SuperAdmin = 'SUPER_ADMIN',
  Compliance = 'COMPLIANCE',
  Valiant = 'VALIANT',
}

export function getUserRoleLabel(role: UserRole): string {
  switch (role) {
    case UserRole.Trader:
      return 'Trader'
    case UserRole.GroupOwner:
      return 'Group Owner'
    case UserRole.SuperAdmin:
      return 'Super Admin'
    case UserRole.Compliance:
      return 'Compliance'
    case UserRole.Valiant:
      return 'Valiant'
    default:
      return role
  }
}

export type AdminUIRole = UserRole.SuperAdmin | UserRole.Compliance
export type UserUIRole = UserRole.Trader | UserRole.GroupOwner
export type ValiantUIRole = UserRole.Valiant

export function isAdminUIRole(role: string): role is AdminUIRole {
  return role === UserRole.SuperAdmin || role === UserRole.Compliance
}

export function isUserUIRole(role: string): role is UserUIRole {
  return role === UserRole.Trader || role === UserRole.GroupOwner
}
export interface AuthTokens<Role extends string = AdminUIRole> {
  token: string
  refreshToken: string
  role: Role
  userId: string
}

export interface MinimalAuthTokens {
  token: string
  refreshToken: string
}

export interface AuthContextType<Role extends string = AdminUIRole> {
  auth: AuthTokens<Role> | null
  isLoading: boolean
  setAuth: React.Dispatch<React.SetStateAction<AuthTokens<Role> | null>>
  login: (username: string, password: string, secret?: string) => Promise<boolean>
  signout: () => void
  resetPassword: (password: string, token: string) => Promise<boolean>
  decodeToken: (token: string) => { role: Role; userId: string }
}

export interface AuthInstance {
  login: (username: string, password: string, secret?: string) => Promise<MinimalAuthTokens>
  adminLogin?: (username: string, password: string, secret?: string) => Promise<MinimalAuthTokens>
  requestMFA: (username: string, password: string) => Promise<string>
  setupMFA: (username: string, password: string, secret: string) => Promise<void>
  resetPassword: (password: string, token: string) => Promise<void>
  refresh: (refreshToken: string) => Promise<MinimalAuthTokens>
  isJwtStale: (jwt: string) => boolean
  loginWithPasskey?: (username: string, password: string) => Promise<MinimalAuthTokens>
  passkeyLoginAvailable?: (username: string, password: string) => Promise<boolean>
  passkeysEnabled?: () => Promise<boolean>
}

export interface LoginProps<Role extends string = AdminUIRole> {
  authInstance: AuthInstance
  authProvider: AuthContextType<Role>
  onLoginSuccess?: () => void
  onAdminLoginSuccess?: (token: string) => void
  variant?: 'user' | 'admin'
}
